/* eslint-disable camelcase */
import { camelCaseKeys } from 'src/utils/camelCaseKeys'

const MISSING_TRANSLATION = 'MISSING TRANSLATION'

export const ROOM_FACILITIES_SOURCE = {
  ROOM_NAME: 'room_by_name',
  ROOM_CODE: 'room_by_code',
  ROOM_MISSING_TRANSLATION: 'room_by_missing_translation',
  DEFAULT: 'defaults',
  NO_SOURCE: 'no_source',
}

export type RoomFacilitiesSourceType =
  (typeof ROOM_FACILITIES_SOURCE)[keyof typeof ROOM_FACILITIES_SOURCE]

export interface ApiFacility {
  alias: string
  code_group: string
  facility_id: string
  featured: boolean
  free: boolean
  name: string
  order: number
  room_code: string
  slug: string
  value: string | null
}

export interface ApiFacilitiesWithCategory {
  code: number | null
  description: string
  facilities: ApiFacility[]
}

export type ApiRoomContent = Record<
  string,
  {
    facilities_room_codes?: string[]
    facilities_source?: string
    facility_groups: ApiFacilitiesWithCategory[]
    images?: any[]
    images_room_codes?: string[]
  }
>

export interface ApiData {
  room_content?: ApiRoomContent
  defaults?: {
    facility_groups: ApiFacilitiesWithCategory[]
  }
}

export type MappedFacility = {
  alias: string
  codeGroup: string
  featured: boolean
  free: boolean
  name: string
  order: number
  slug: string
  value: string | null
  icon: string
}

export type MappedClusteredFacilities = {
  code: number | null
  description: string
  facilities: MappedFacility[]
}

function mapFacility(facility: ApiFacility): MappedFacility {
  const camelCasedFacility = camelCaseKeys(facility) as {
    alias: string
    codeGroup: string
    facilityId: string
    featured: boolean
    free: boolean
    icon?: string
    name: string
    order: number
    slug: string
    value: string | null
  }
  const { alias, codeGroup, facilityId, featured, free, icon, name, order, slug, value } =
    camelCasedFacility
  return {
    alias,
    codeGroup,
    free,
    featured,
    icon:
      (icon && /base64-icon/.test(icon) && icon.replace('base64-icon ', '')) ||
      alias ||
      facilityId ||
      'done',
    name,
    order,
    slug,
    value,
  }
}

function mapFacilityWithCategory(
  apiGroupedFacilities: ApiFacilitiesWithCategory[]
): MappedClusteredFacilities[] {
  return apiGroupedFacilities.map(group => ({
    code: group.code,
    description: group.description,
    facilities: group.facilities.map((facility: ApiFacility) => mapFacility(facility)),
  }))
}

export function buildRoomFacilitiesObject(
  facilities: MappedClusteredFacilities[],
  source: RoomFacilitiesSourceType
) {
  return {
    facilities,
    source,
  }
}

export function getFacilitiesByCode(
  code: string,
  facilities: ApiFacilitiesWithCategory[] = []
): MappedClusteredFacilities[] | null {
  const filteredByCodeFacilities = facilities.map(group => ({
    code: group.code,
    description: group.description,
    facilities: group.facilities
      .filter(facility => facility.room_code?.toLowerCase() === code.toLowerCase())
      .map((facility: ApiFacility) => mapFacility(facility)),
  }))

  return filteredByCodeFacilities.filter(group => group.facilities.length > 0)
}

export function getFacilitiesByRoom(
  roomContent: ApiRoomContent,
  { name, code }: { name: string; code: string }
) {
  if (!roomContent) {
    return buildRoomFacilitiesObject([], ROOM_FACILITIES_SOURCE.NO_SOURCE)
  }

  if (roomContent?.[name]?.facility_groups?.length) {
    return buildRoomFacilitiesObject(
      mapFacilityWithCategory(roomContent[name].facility_groups),
      ROOM_FACILITIES_SOURCE.ROOM_NAME
    )
  }

  const filteredRoomsWithFacilities = Object.keys(roomContent).filter(roomName => {
    const room = roomContent[roomName]
    const roomCodes = room?.facilities_room_codes ?? []
    const hasFacilities = room?.facility_groups?.length > 0
    const lowerCaseCode = code.toLowerCase()
    const isCodeInRoomCodes = roomCodes.some(
      (roomCode: string) => roomCode.toLowerCase() === lowerCaseCode
    )
    return isCodeInRoomCodes && hasFacilities
  })
  if (filteredRoomsWithFacilities.length) {
    const roomName = filteredRoomsWithFacilities?.[0]
    return buildRoomFacilitiesObject(
      mapFacilityWithCategory(roomContent[roomName].facility_groups),
      ROOM_FACILITIES_SOURCE.ROOM_CODE
    )
  }

  const facilities = roomContent?.[MISSING_TRANSLATION]?.facility_groups
  const clusteredFacilitiesByCode = getFacilitiesByCode(code, facilities)
  if (clusteredFacilitiesByCode?.length) {
    return buildRoomFacilitiesObject(
      clusteredFacilitiesByCode,
      ROOM_FACILITIES_SOURCE.ROOM_MISSING_TRANSLATION
    )
  }

  return buildRoomFacilitiesObject([], ROOM_FACILITIES_SOURCE.NO_SOURCE)
}

export function getRoomFacilities(roomInfo: { name: string; code: string }, data: ApiData) {
  if (!data.room_content) {
    if (data?.defaults?.facility_groups) {
      return buildRoomFacilitiesObject(
        mapFacilityWithCategory(data.defaults.facility_groups),
        ROOM_FACILITIES_SOURCE.DEFAULT
      )
    }
    return buildRoomFacilitiesObject([], ROOM_FACILITIES_SOURCE.NO_SOURCE)
  }

  const roomFacilities = getFacilitiesByRoom(data.room_content, roomInfo)
  if (roomFacilities.facilities.length) {
    return roomFacilities
  }

  if (data?.defaults?.facility_groups) {
    return buildRoomFacilitiesObject(
      mapFacilityWithCategory(data.defaults.facility_groups),
      ROOM_FACILITIES_SOURCE.DEFAULT
    )
  }

  return buildRoomFacilitiesObject([], ROOM_FACILITIES_SOURCE.NO_SOURCE)
}
